// Small pure helpers shared by screens.

export const LAMPORTS_PER_SOL = 1_000_000_000n;

/** Explorer link base for the configured cluster, or null for a local shim/validator (nothing to link to). */
export function explorerBase(rpcUrl: string, kind: 'address' | 'tx'): string | null {
  let host = '';
  try {
    host = new URL(rpcUrl).hostname;
  } catch {
    return null;
  }
  if (host === 'localhost' || host === '127.0.0.1') return null;
  const cluster = host.includes('devnet') ? '?cluster=devnet' : host.includes('testnet') ? '?cluster=testnet' : '';
  return `https://explorer.solana.com/${kind}/` + (cluster ? `\u0000${cluster}` : '');
}

/** `explorerBase` inserts the id where the NUL marker sits; this resolves it. */
export function explorerUrl(base: string | null, id: string): string | null {
  if (!base) return null;
  return base.includes('\u0000') ? base.replace('\u0000', id) : base + id;
}

/** Slots to a rough wall-clock sentence at 400 ms per slot. */
export function slotsToText(slots: bigint): string {
  const seconds = Number(slots) * 0.4;
  if (seconds < 90) return `about ${Math.max(1, Math.round(seconds))} s`;
  if (seconds < 5400) return `about ${Math.round(seconds / 60)} min`;
  return `about ${(seconds / 3600).toFixed(1)} h`;
}

export function shortSig(sig: string): string {
  return `${sig.slice(0, 6)}…${sig.slice(-6)}`;
}

export function whenText(ms: number): string {
  return new Date(ms).toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
}

/** Raw units from a decimal string with no floating point: split on the point, pad or reject the fraction. Null when the text is not a plain decimal or has too many places. */
export function parseUnits(input: string, decimals: number): bigint | null {
  const trimmed = input.trim().replace(/,/g, '');
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const [whole, frac = ''] = trimmed.split('.');
  if (frac.length > decimals) return null;
  return BigInt(whole || '0') * 10n ** BigInt(decimals) + BigInt(frac.padEnd(decimals, '0') || '0');
}

/** Passphrase strength as a sentence, never a bar (docs/08 5.1 step 4). */
export function passphraseStrength(p: string): { ok: boolean; text: string } {
  const n = p.length;
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(p)).length;
  if (n === 0) return { ok: false, text: 'At least 10 characters. A few unrelated words work well.' };
  if (n < 10) return { ok: false, text: `Too short: ${n} of at least 10 characters.` };
  if (n < 14 && classes < 3) return { ok: true, text: 'Acceptable. Longer, or more varied, would be stronger.' };
  if (n < 20) return { ok: true, text: 'Good.' };
  return { ok: true, text: 'Strong.' };
}
